import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { ListItemIcon } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ProfileSettingsDialog from "../../../components/profile/ProfileSettingsDialog";
import {
  JWT_TOKEN,
  USER_EMAIL_ID,
  USER_ID,
  USER_NAME,
  USER_ORG_ID,
} from "../../../Constants/StorageConstants";
import {
  getUserEmail,
  getUserName,
  getUserProfileImage,
} from "../../../services/cacheService";
import MenuButton from "../dashboard/components/MenuButton";
import MenuContent from "../dashboard/components/MenuContent";

const drawerWidth = 240;
const collapsedDrawerWidth = 70; // Must match Layout.js if not passed as prop

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})(({ theme, isCollapsed }) => ({
  width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
  flexShrink: 0,
  boxSizing: "border-box",
  // whiteSpace: "nowrap", // Removed: Better to handle text wrapping on specific elements
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: isCollapsed
      ? theme.transitions.duration.leavingScreen
      : theme.transitions.duration.enteringScreen,
  }),
  [`& .${drawerClasses.paper}`]: {
    width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
    boxSizing: "border-box",
    overflowX: "visible", // Changed from "hidden" to "visible" to allow button to extend
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: isCollapsed
        ? theme.transitions.duration.leavingScreen
        : theme.transitions.duration.enteringScreen,
    }),
  },
}));

export default function AppSideMenu({ isCollapsed, toggleSidebar }) {
  const [open, setOpen] = useState(false);
  const userName = getUserName();
  const userEmail = getUserEmail();
  const profileImage = getUserProfileImage();

  const theme = useTheme();
  const navigate = useNavigate();

  const collapsedStateLogo =
    theme.palette.mode === "dark"
      ? "/adaptixLogoDark.png"
      : "/adaptixLogoLight.png";

  const expandStateLogo =
    theme.palette.mode === "dark"
      ? "/adaptixTextLogoDark.png"
      : "/adaptixTextLogoLight.png";

  const logoUrl = isCollapsed ? collapsedStateLogo : expandStateLogo;

  return (
    <Drawer
      variant="permanent"
      isCollapsed={isCollapsed}
      sx={{
        display: { xs: "none", md: "block" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          pt: "10px",
          minHeight: "56px",
          boxSizing: "border-box",
        }}
      >
        <img
          src={process.env.PUBLIC_URL + logoUrl}
          alt="Company Logo"
          style={
            isCollapsed
              ? {
                  width: "60px",
                  height: "60px",
                }
              : { width: "-webkit-fill-available" }
          }
        />
      </Box>
      <Divider />
      <Box
        sx={{
          "overflow-x": "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MenuContent isCollapsed={isCollapsed} />
      </Box>
      <Stack
        direction={isCollapsed ? "column" : "row"}
        sx={{
          p: isCollapsed ? 1 : 2,
          gap: 0.5,
          alignItems: "center",
          borderTop: "1px solid",
          justifyContent: isCollapsed ? "center" : "initial",
          borderColor: "divider",
        }}
      >
        <Avatar
          alt={userName}
          src={profileImage}
          sx={{ width: 36, height: 36, mb: isCollapsed ? 0.5 : 0 }}
          onClick={() => setOpen(true)}
        />
        <ProfileSettingsDialog open={open} onClose={() => setOpen(false)} />
        {!isCollapsed && (
          <Box sx={{ mr: "auto", ml: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 500,
                lineHeight: "16px",
                maxWidth: 120,
                display: "block",
              }}
            >
              {userName}
            </Typography>
            <Tooltip title={userEmail} placement="top">
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {userEmail}
              </Typography>
            </Tooltip>
          </Box>
        )}
        <MenuButton
          onClick={() => {
            localStorage.removeItem(JWT_TOKEN);
            localStorage.removeItem(USER_NAME);
            localStorage.removeItem(USER_ID);
            localStorage.removeItem(USER_ORG_ID);
            localStorage.removeItem(USER_EMAIL_ID);
            navigate("/login", { replace: true });
          }}
          sx={{
            borderColor: "rgb(221, 221, 221)",
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
        </MenuButton>
      </Stack>
      <Box
        sx={{
          position: "fixed",
          left: isCollapsed ? "57px" : "228px",
          top: "60px",
          display: { xs: "none", md: "block" },
          transition: theme.transitions.create("left", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <IconButton
          color="inherit"
          aria-label="toggle sidebar"
          onClick={toggleSidebar}
          sx={{
            height: 24,
            width: 24,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "50%",
            boxShadow: 1,
            transition: theme.transitions.create(
              ["background-color", "box-shadow"],
              {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }
            ),
            "&:hover": {
              backgroundColor: "hsl(209.84deg 78.06% 46.47%)",
            },
          }}
        >
          {isCollapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Drawer>
  );
}
